import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@/components/ui/Modal";
import { SpinnerIcon } from "@/components/icons";
import { apiClient } from "@/services/apiClient";
import { Post, PostStatus } from "@/core/types/postTypes";
import { Idea, IdeaStatus } from "@/domains/Ideas/types";
import { getIdeaConcept } from "@/domains/Ideas/ideaHelpers";
import { IdeaCard } from "@/domains/Ideas/components/IdeaCard";
import { SelectablePostCard } from "@/domains/PostCreator/cards/SelectablePostCard";
import { useEntityLinks, EntityLink } from "../hooks/useEntityLinks";

type TabType = "posts" | "ideas";

interface LinkResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  eventId: string;
  eventName: string;
  existingLinks: EntityLink[];
  onLinked: () => void;
}

const EXCLUDED_POST_STATUSES = new Set([
  PostStatus.PUBLISHED,
  PostStatus.PARTIALLY_PUBLISHED,
  PostStatus.ARCHIVED,
]);

const EXCLUDED_IDEA_STATUSES = new Set<IdeaStatus>([
  "REJECTED",
  "COMPLETE",
]);

export const LinkResourceModal: React.FC<LinkResourceModalProps> = ({
  isOpen,
  onClose,
  clientId,
  eventId,
  eventName,
  existingLinks,
  onLinked,
}) => {
  const [tab, setTab] = useState<TabType>("posts");
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [posts, setPosts] = useState<Post[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  const { createLink } = useEntityLinks(onLinked);

  const alreadyLinkedIds = useMemo(() => {
    return new Set(existingLinks.map((l) => l.targetId));
  }, [existingLinks]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setSearch("");
      return;
    }

    setLoading(true);
    const fetchData = async () => {
      try {
        if (tab === "posts") {
          const response = await apiClient.get<{ posts: Post[] }>(
            `/client/${clientId}/posts`,
          );
          setPosts(response.posts || []);
        } else {
          const response = await apiClient.get<{ ideas: Idea[] }>(
            `/api/clients/${clientId}/ideas`,
          );
          setIdeas(response.ideas || []);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isOpen, tab, clientId]);

  const filteredPosts = useMemo(() => {
    const q = search.toLowerCase();
    return posts.filter((p) => {
      if (EXCLUDED_POST_STATUSES.has(p.status)) return false;
      if (alreadyLinkedIds.has(p.id)) return false;
      if (!q) return true;
      return (p.caption || "").toLowerCase().includes(q)
        || (p.groupName || "").toLowerCase().includes(q);
    });
  }, [posts, search, alreadyLinkedIds]);

  const filteredIdeas = useMemo(() => {
    const q = search.toLowerCase();
    return ideas.filter((idea) => {
      if (EXCLUDED_IDEA_STATUSES.has(idea.status)) return false;
      if (alreadyLinkedIds.has(idea.id)) return false;
      if (!q) return true;
      return idea.title.toLowerCase().includes(q)
        || (getIdeaConcept(idea) || "").toLowerCase().includes(q);
    });
  }, [ideas, search, alreadyLinkedIds]);

  const handleLink = async () => {
    if (!selectedId) return;
    setLinking(true);
    try {
      await createLink({
        sourceType: "calendar_event",
        sourceId: eventId,
        targetType: tab === "posts" ? "post" : "idea",
        targetId: selectedId,
        year,
      });
      onClose();
    } catch {
      // silent
    } finally {
      setLinking(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      header={
        <div>
          <h2 className="text-lg font-semibold">Link resource</h2>
          <p className="text-sm text-gray-500 mt-0.5">{eventName}</p>
        </div>
      }
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={!selectedId || linking}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {linking && <SpinnerIcon className="w-4 h-4" />}
            Link
          </button>
        </>
      }
    >
      <div className="p-4 space-y-4">
        {/* Year selector + tabs */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-1">
            {(["posts", "ideas"] as TabType[]).map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  setSelectedId(null);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === t
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t === "posts" ? "Posts" : "Ideas"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Year:</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-md px-2 py-1"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder={tab === "posts" ? "Search by caption..." : "Search by title..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {/* Item list */}
        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <SpinnerIcon className="w-5 h-5 text-blue-500" />
            </div>
          ) : tab === "posts" ? (
            filteredPosts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">
                No posts available to link
              </p>
            ) : (
              filteredPosts.map((post) => (
                <SelectablePostCard
                  key={post.id}
                  post={post}
                  selected={selectedId === post.id}
                  onSelect={() => setSelectedId(post.id)}
                />
              ))
            )
          ) : filteredIdeas.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              No ideas available to link
            </p>
          ) : (
            filteredIdeas.map((idea) => (
              <div
                key={idea.id}
                onClick={() => setSelectedId(idea.id)}
                className={`cursor-pointer rounded-xl transition-all duration-150 ${
                  selectedId === idea.id
                    ? "ring-2 ring-blue-500 ring-offset-1"
                    : "hover:ring-1 hover:ring-gray-300"
                }`}
              >
                <IdeaCard idea={idea} />
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};
